import React, { useState, useRef, useEffect } from "react";

export function DualRangeSlider({
  min = 0,
  max = 1000,
  step = 1,
  initialMin = min,
  initialMax = max,
  onChange = () => {},
  refreshParams = () => {},
}) {
  const [minVal, setMinVal] = useState(initialMin);
  const [maxVal, setMaxVal] = useState(initialMax);

  const sliderRef = useRef(null);
  const dragging = useRef(null); // "min" | "max" | "range"

  const getPercent = (value) => (value - min) / (max - min);

  const getValueFromPosition = (clientX) => {
    const rect = sliderRef.current.getBoundingClientRect();
    let percent = (clientX - rect.left) / rect.width;
    percent = Math.max(0, Math.min(1, percent));
    let value = min + percent * (max - min);

    // snap to step
    value = Math.round(value / step) * step;

    return Math.max(min, Math.min(max, value));
  };

  const onMouseMove = (e) => {
    if (!dragging.current) return;

    const value = getValueFromPosition(e.clientX);

    if (dragging.current === "min") {
      setMinVal((prev) => Math.min(value, maxVal - step));
    }

    if (dragging.current === "max") {
      setMaxVal((prev) => Math.max(value, minVal + step));
    }

    if (dragging.current === "range") {
      const delta = value - (minVal + maxVal) / 2;
      let newMin = minVal + delta;
      let newMax = maxVal + delta;

      if (newMin < min) {
        newMax += min - newMin;
        newMin = min;
      }

      if (newMax > max) {
        newMin -= newMax - max;
        newMax = max;
      }

      setMinVal(Math.round(newMin / step) * step);
      setMaxVal(Math.round(newMax / step) * step);
    }
  };

  const stopDragging = () => {
    dragging.current = null;
    refreshParams(minVal, maxVal)
  };

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stopDragging);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stopDragging);
    };
  });

  useEffect(() => {
      onChange({ min: minVal, max: maxVal })
  }, [minVal, maxVal]);

  const minPercent = getPercent(minVal) * 100;
  const maxPercent = getPercent(maxVal) * 100;

  return (
    <div className="w-full max-w-xl mx-auto select-none">
      {/* Labels */}
      <div className="flex justify-between mb-4 text-sm font-medium text-gray-700">
        <div className="px-3 py-1 bg-gray-100 rounded-lg shadow-sm">
          {minVal}€
        </div>
        <div className="px-3 py-1 bg-gray-100 rounded-lg shadow-sm">
          {maxVal}€{maxVal == max ? " +" : " "}
        </div>
      </div>

      {/* Slider */}
      <div
        ref={sliderRef}
        className="relative h-2 bg-gray-200 rounded-full cursor-pointer"
      >
        {/* Active range */}
        <div
          className="absolute h-2 bg-green rounded-full cursor-grab"
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`,
          }}
          onMouseDown={() => (dragging.current = "range")}
        />

        {/* Min thumb */}
        <div
          className="absolute w-5 h-5 bg-white border border-gray-300 rounded-full shadow cursor-pointer hover:scale-110 transition"
          style={{
            left: `${minPercent}%`,
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
          onMouseDown={() => (dragging.current = "min")}
        />

        {/* Max thumb */}
        <div
          className="absolute w-5 h-5 bg-white border border-gray-300 rounded-full shadow cursor-pointer hover:scale-110 transition"
          style={{
            left: `${maxPercent}%`,
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
          onMouseDown={() => (dragging.current = "max")}
        />
      </div>
    </div>
  );
}